import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ReferenceCandidateAdapter } from 'components/ReferenceCandidates/types';
import {
  AdditionalReference,
  NormalizedLoad,
  TMSEquipmentType,
} from 'types/Load';

type MGBReferenceCandidateAdapterArgs = {
  // The tenant's selectable MercuryGate reference types (system-managed types removed).
  referenceTypes: TMSEquipmentType[];
  additionalReferences: AdditionalReference[];
  appendReference: (reference: AdditionalReference) => void;
};

// The row "+ Add Reference" appends. weight and pieces stay unset until the rep fills them.
export const emptyAdditionalReference: AdditionalReference = {
  qualifier: '',
  number: '',
  weight: undefined as unknown as number,
  pieces: undefined as unknown as number,
  shouldSendToDriver: false,
};

// Lets reference candidates become MercuryGate additional references: each one is a new
// Type + Value card, the same card "+ Add Reference" creates.
export function useMGBReferenceCandidateAdapter({
  referenceTypes,
  additionalReferences,
  appendReference,
}: MGBReferenceCandidateAdapterArgs): ReferenceCandidateAdapter {
  const { control } = useFormContext<NormalizedLoad>();
  const [
    stops,
    poNums,
    customerRefNumber,
    pickupRefNumber,
    consigneeRefNumber,
  ] = useWatch({
    control,
    name: [
      'stops',
      'poNums',
      'customer.refNumber',
      'pickup.refNumber',
      'consignee.refNumber',
    ],
  });

  const referenceTypeOptions = useMemo(
    () =>
      referenceTypes.map((type) => ({
        value: type.id,
        label: type.name || type.id,
      })),
    [referenceTypes]
  );

  const usedNumbers = useMemo(
    () => [
      ...additionalReferences.map((reference) => reference?.number ?? ''),
      ...(stops ?? []).map((stop) => stop?.refNumber ?? ''),
      poNums ?? '',
      customerRefNumber ?? '',
      pickupRefNumber ?? '',
      consigneeRefNumber ?? '',
    ],
    [
      additionalReferences,
      stops,
      poNums,
      customerRefNumber,
      pickupRefNumber,
      consigneeRefNumber,
    ]
  );

  const appendRow = (number: string, qualifier: string) =>
    appendReference({ ...emptyAdditionalReference, qualifier, number });

  return {
    referenceTypes: referenceTypeOptions,
    usedNumbers,
    addReference: appendRow,
    // The row's Type stays empty, and required, for the rep to pick.
    startReference: (number) => appendRow(number, ''),
  };
}
